import React from 'react'
import { ValueType } from 'react-select'
import { toast } from 'react-toastify'
import isObjEqual from 'fast-deep-equal'
import { CardName, Deck, PlayingCard, Suit } from "../../Cards"
import { CardRepresentation } from "../../Images/images"
import { CardSet, nullRoom, Player, Room } from '../../Types'
import { callBluff, getLastPlayedCardSet, getLastPlayedCardSetWithCards, moveCardsWithinRoom } from '../utils'
import { PlayFunction } from 'use-sound/dist/types'

export type OptionType = {
    value: string,
    label: string
}

export const cardOptions:OptionType[] = [
    {value: 'Ace', label: 'Ace'},
    {value: 'Two', label: 'Two'},
    {value: 'Three', label: 'Three' },
    {value: 'Four', label: 'Four'},
    {value: 'Five', label: 'Five'},
    {value: 'Six', label: 'Six'},
    {value: 'Seven', label: 'Seven'},
    {value: 'Eight', label: 'Eight'},
    {value: 'Nine', label: 'Nine'},
    {value: 'Ten', label: 'Ten'},
    {value: 'Jack', label: 'Jack'},
    {value: 'Queen', label: 'Queen'},
    {value: 'King', label: 'King'}
]

export const allCardOptions:OptionType[] = [
    {value: 'Ace', label: 'Ace'},
    {value: 'Two', label: 'Two'},
    {value: 'Three', label: 'Three' },
    {value: 'Four', label: 'Four'},
    {value: 'Five', label: 'Five'},
    {value: 'Six', label: 'Six'},
    {value: 'Seven', label: 'Seven'},
    {value: 'Eight', label: 'Eight'},
    {value: 'Nine', label: 'Nine'},
    {value: 'Ten', label: 'Ten'},
    {value: 'Jack', label: 'Jack'},
    {value: 'Queen', label: 'Queen'},
    {value: 'King', label: 'King'},
    {value: 'Joker', label: 'Select a Card Value to Play'}
]

// Used to get the CardName from the Select UI component, which is String based
export const getCardNameFromSelect = (selectedOption:any):CardName => {
    switch(selectedOption.label) {
        case "Ace":
            return CardName.Ace
        case "Two":
            return CardName.Two
        case "Three":
            return CardName.Three
        case "Four":
            return CardName.Four
        case "Five":
            return CardName.Five
        case "Six":
            return CardName.Six
        case "Seven":
            return CardName.Seven
        case "Eight":
            return CardName.Eight
        case "Nine":
            return CardName.Nine
        case "Ten":
            return CardName.Ten
        case "Jack":
            return CardName.Jack
        case "Queen":
            return CardName.Queen
        case "King":
            return CardName.King
        default:
            return CardName.Joker
    }
}

export const isAnyCardSelected = (selectedCards:boolean[]) => {
    for (let selectedCardIndex = 0; selectedCardIndex < selectedCards.length; selectedCardIndex++) {
        if (selectedCards[selectedCardIndex]) {
            return true
        }
    }
    return false
}

export const findNumCardsSelected = (selectedCards:boolean[]) => {
    let count = 0;
    for (let selectedCardIndex = 0; selectedCardIndex < selectedCards.length; selectedCardIndex++) {
        if (selectedCards[selectedCardIndex]) {
            count += 1
        }
    }
    return count
}

/**
 * May want to make a large-scale object that contains all the relevant state variables from
 * Game and import that everywhere (would clean up function signatures)
 */

// Provides the information for showing how many cards opponents have
export const showOpponentHands = (room:Room, username:string) => {
    let message = []
    for (let playerIndex = 0; playerIndex < room.order.length; playerIndex++) {
        const player = room.order[playerIndex]
        message.push(
            <div>
                {`${playerIndex + 1}: ${player.playerId} ${player.playerId === username ? "(you)" : ""} has ${player.hand.deck.getCount()} card${player.hand.deck.getCount() > 1 ? "s": ""}.`}
            </div>
        )
    }
    return message
}

// Provides the visual information to show that a card has been selected
export const changeBorder = (htmlElem: HTMLElement | null, selected:boolean) => {
    if (htmlElem != null) {
        if (selected) {
            htmlElem.style.outline = '5px solid yellow'
            return 1
        } else {
            htmlElem.style.outline = ''
            return -1
        }
    }
    return 0
}

// Creates an map of cardnames to JSX elements containing what needs to be rendered
export const groupCards = (
    cards:PlayingCard[], 
    selectedCards:boolean[], 
    setCards:(value: React.SetStateAction<PlayingCard[]>) => void, 
    setSelectedCards:(value: React.SetStateAction<boolean[]>) => void,
    numCardsSelected:number,
    setNumCardsSelected:React.Dispatch<React.SetStateAction<number>>
) => {
    let groupedCards: { [cardtype: number]: JSX.Element[]; } = {}
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
        let entry = groupedCards[cards[cardIndex].cardName]
        if (entry === undefined) {
            entry = []
        }
        entry.push(
            <span
                id={`card${cardIndex.toString()}`}
                onClick={() => {
                    selectedCards[cardIndex] = !selectedCards[cardIndex]
                    setCards(cards)
                    setSelectedCards(selectedCards)
                    const changeNumSelectedCards = changeBorder(document.getElementById(`card${cardIndex.toString()}`), selectedCards[cardIndex])
                    setNumCardsSelected(numCardsSelected + changeNumSelectedCards)
                }}
                style={{
                    outline: `${selectedCards[cardIndex] ? '5px solid yellow' : ''}`,
                    margin: 8,
                    flexGrow: 1,
                    display: "inline-block",
                    lineHeight: 0
                }}
                >
                {CardRepresentation(cards[cardIndex].cardName, cards[cardIndex].suit)}
            </span>
        )
        groupedCards[cards[cardIndex].cardName] = entry
    }
    return groupedCards
}

// OnClick methods

// Because of toast issues, playOnClick isn't defined here. Fix should come later to address
// these concerns

export const bluffOnClick = (
    room:Room, 
    player:Player | undefined, 
    gameId:string, 
    username:string,
    setRoom:React.Dispatch<React.SetStateAction<Room>>,
    setBluffCalled:(value: React.SetStateAction<boolean>) => void
) => {
    let initNum = 0
    let lastPlayer = ""
    if (player === undefined) {
        return new Promise(()=>{return {room: nullRoom, message: ""}})
    } else {
        return callBluff(room.gameId, player).then(success => {
            if (!success) {
                // beaten to the punch by another player
                return nullRoom
            } else {
                room.bluffCaller = player
                const lastPlayedCardSet = getLastPlayedCardSetWithCards(room)
                initNum = player.hand.deck.getCount()
                lastPlayer = lastPlayedCardSet.player
                setBluffCalled(true)
                // render update to page here (informing of the calling of bluff)
                // move cards and wait concurrently before explaining what happened
                // and continuing
                return moveCardsWithinRoom(room)
            }
        }).then((newRoom) => {
            if (isObjEqual(nullRoom, newRoom)) {
                return {room: nullRoom, message: ""}
            }
            // at this stage, the bluffcaller IS the current player
            let num = 0
            for (let i = 0; i < newRoom.order.length; i++) {
                if (newRoom.order[i].playerId === username) {
                    num = newRoom.order[i].hand.deck.getCount()
                    break
                }
            }
            let message = ""
            const truthMessages = [
                "Looks like you were wrong this time. Want to show everyone the deck or take your shame in silence?",
                "You should really be more trusting! They didn't lie this time. Want to show the deck so everyone can have a laugh?",
                "Oh dear, you seem quite paranoid. Not everyone lies. Want to show everyone what was played or keep it a secret?",
                "You know you could have passed, right? You didn't HAVE to call bluff. Want to show the deck to all so we can all learn from this lesson?",
                "Oof, that's a critical miss on your perception check! They were telling the truth. Show the deck to prove it?",
                "Well, well, well... looks like someone forgot their detective badge at home. Want to show the evidence of your mistake?",
                "Congratulations! You've won absolutely nothing except these cards. Care to display your trophy?",
                "Not exactly a 200 IQ play there. They were honest this time. Want everyone to witness your blunder?",
                "Your Spidey-sense needs some recalibration - they weren't lying! Show the deck to confirm your wrong hunch?",
                "Trust issues much? They played fair and square this round. Want to broadcast your suspicions were unfounded?",
                "Yikes, that backfired spectacularly. Maybe trust people occasionally? Show the deck or hide the evidence?",
                "Achievement Unlocked: Wrong Call. They were truthful. Display your accomplishment to all?",
                "Plot twist: they were actually honest! Didn't see that coming, did you? Reveal the cards?",
                "Your bluff-detection radar is broken. They told the truth. Want to show everyone the malfunction?",
                "Like Wile E. Coyote with an ACME detector - completely wrong! Show the deck to seal your fate?",
                "Remember: with great power comes great responsibility. You had neither here. Reveal the cards?",
                "That was about as accurate as a stormtrooper's aim. They were honest. Show the proof?",
                "Your intuition rolled a natural 1. They weren't lying. Display your critical failure?",
                "Gonna cry? They were telling the truth and now you're stuck with these cards. Show the deck?",
                "I missed the part where that's your problem... oh wait, it IS your problem. They didn't lie. Reveal the cards?",
                "Stings, doesn't it? Being wrong about the bluff call. Want to show everyone the deck that's now yours?",
                "Look at little detective junior. Gonna cry? They told the truth. Show everyone your mistake?"
            ]
            const lieMessages = [
                "Looks like you were right to doubt! Want to show everyone the deck so they can all see the lies?",
                "That sneaky player! Good on you to call them on their BS (Brussel Sprouts). Want to show everyone the deck to show all how wrong they are?",
                "Does it hurt? To have such an insight into your opponent's psyche that you can FEEL the lies? Additionally, do you want to show the deck to show how good you are at figuring it out?",
                "Sounds like people should call you the \"Oracle of our Time\" as you seem to have nailed the liar. Want to show the deck to shame them?",
                "Gotcha! You caught them red-handed like a cartoon villain in a trap. Reveal their deception to all?",
                "Your deception detection skill is maxed out! They were totally lying. Want to expose the fraud?",
                "Sherlock Holmes would be proud. You've cracked the case! Show the evidence to prove it?",
                "What a play! You just pulled off a pro gamer move. Want to show everyone why you're the MVP?",
                "They played you like a fiddle, but you weren't dancing. Nice catch! Display their dishonesty?",
                "You see through illusions better than Scooby-Doo unmasking villains. Reveal the truth?",
                "Critical hit on your insight check! They were definitely bluffing. Show everyone your victory?",
                "You've got the perception of a hawk and the wisdom of an owl. They lied. Prove it to everyone?",
                "Busted! They thought they could fool you, but you're too sharp. Want to showcase their lies?",
                "Your lie detector is calibrated perfectly. They tried to deceive you and failed. Reveal all?",
                "Like catching Team Rocket in the act - they're busted! Want to show everyone the proof?",
                "You rolled a natural 20 on that perception check! They were lying through their teeth. Display the evidence?",
                "Achievement Unlocked: Master Detective. You saw right through them. Share your triumph?",
                "They thought they were slick, but you were slicker. Caught them like a boss! Show the deck?",
                "I'm gonna put some dirt in your eye! Oh wait, you already did that to them by catching their lie. Show the deck?",
                "See ya, chump! That's what you can say to that liar. They got caught. Want to show everyone the proof?",
                "You want forgiveness? Get religion. But you don't need forgiveness - you were RIGHT. Display their lies to all?",
                "Now dig on this - you just caught them lying! Stings, doesn't it? (For them, not you). Show the deck?"
            ]
            if (num > initNum) {
                // truth was told
                newRoom.bluffStatus = `A bluff has been called! ${newRoom.bluffCaller.playerId} was forced to pick up the deck! They should really be more trustworthy; ${lastPlayer} didn't lie (this time).`
                message = truthMessages[Math.floor(Math.random() * truthMessages.length)]
            } else {
                // lies were told
                newRoom.bluffStatus = `A bluff has been called! ${lastPlayer} was forced to pick up! ${newRoom.bluffCaller.playerId} was right to doubt!`
                message = lieMessages[Math.floor(Math.random() * lieMessages.length)]
            }
            return {room: newRoom, message: message}
        })
    }   
}

// Render related Methods

export const renderBluffCards = (cardsets:CardSet[]) => {
    if (!cardsets || cardsets.length === 0) {
        return <div style={{ color: "#94a3b8" }}>No cards to show</div>
    }

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "20px",
            maxHeight: "70vh",
            overflowY: "auto"
        }}>
            <div style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#60a5fa",
                textAlign: "center",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "1px"
            }}>
                Round History
            </div>

            {cardsets.map((cardset, cardsetIndex) => {
                const cards = cardset.deck.getCards()
                const isLatest = cardsetIndex === cardsets.length - 1
                const isFirst = cardsetIndex === 0

                return (
                    <div
                        key={`cardset-${cardsetIndex}`}
                        style={{
                            backgroundColor: isLatest
                                ? "rgba(96, 165, 250, 0.15)"
                                : "rgba(255, 255, 255, 0.05)",
                            borderRadius: "12px",
                            padding: "16px",
                            border: isLatest
                                ? "2px solid rgba(96, 165, 250, 0.5)"
                                : "1px solid rgba(255, 255, 255, 0.1)",
                            position: "relative"
                        }}
                    >
                        {isLatest && (
                            <div style={{
                                position: "absolute",
                                top: "-10px",
                                right: "12px",
                                backgroundColor: "#60a5fa",
                                color: "#ffffff",
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                            }}>
                                Latest
                            </div>
                        )}
                        {isFirst && !isLatest && (
                            <div style={{
                                position: "absolute",
                                top: "-10px",
                                right: "12px",
                                backgroundColor: "#8b5cf6",
                                color: "#ffffff",
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                            }}>
                                First
                            </div>
                        )}

                        <div style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#ffffff",
                            marginBottom: "12px"
                        }}>
                            {cardset.player}
                        </div>

                        {cards.length !== 0 ? (
                            <div style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                justifyContent: "center"
                            }}>
                                {cards.map((card, idx) => (
                                    <span
                                        key={`card-${cardsetIndex}-${idx}`}
                                        style={{
                                            display: "inline-block",
                                            lineHeight: 0,
                                            transform: "scale(0.8)",
                                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                                        }}
                                    >
                                        {CardRepresentation(card.cardName, card.suit)}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: "center",
                                color: "#94a3b8",
                                fontStyle: "italic",
                                padding: "12px"
                            }}>
                                Passed
                            </div>
                        )}
                    </div>
                )
            }).reverse()}
        </div>
    )
}

// Render cards that have been selected - compact mini view
export const renderSelectedCards = (
    cards:PlayingCard[],
    selectedCards:boolean[],
    setCards:(value: React.SetStateAction<PlayingCard[]>) => void,
    setSelectedCards:(value: React.SetStateAction<boolean[]>) => void,
    numCardsSelected:number,
    setNumCardsSelected:React.Dispatch<React.SetStateAction<number>>
) => {
    let retArr = []

    // Helper to get card name display
    const getCardDisplay = (cardName: CardName): string => {
        switch(cardName) {
            case CardName.Ace: return "A"
            case CardName.Two: return "2"
            case CardName.Three: return "3"
            case CardName.Four: return "4"
            case CardName.Five: return "5"
            case CardName.Six: return "6"
            case CardName.Seven: return "7"
            case CardName.Eight: return "8"
            case CardName.Nine: return "9"
            case CardName.Ten: return "10"
            case CardName.Jack: return "J"
            case CardName.Queen: return "Q"
            case CardName.King: return "K"
            case CardName.Joker: return "JKR"
            default: return "?"
        }
    }

    // Helper to get suit symbol and color
    const getSuitInfo = (suit: Suit): { symbol: string, color: string } => {
        switch(suit) {
            case Suit.Hearts: return { symbol: "♥", color: "#ef4444" }
            case Suit.Diamonds: return { symbol: "♦", color: "#ef4444" }
            case Suit.Clubs: return { symbol: "♣", color: "#1f2937" }
            case Suit.Spades: return { symbol: "♠", color: "#1f2937" }
            default: return { symbol: "", color: "#1f2937" }
        }
    }

    for (let i = 0; i < cards.length; i++) {
        const selCard = cards[i]
        if (selectedCards[i]) {
            const suitInfo = getSuitInfo(selCard.suit)
            retArr.push(
                <div
                    key={`selected-card-${i}`}
                    onClick={() => {
                        // Deselect the card when clicked
                        selectedCards[i] = false
                        setCards(cards)
                        setSelectedCards(selectedCards)
                        changeBorder(document.getElementById(`card${i.toString()}`), false)
                        setNumCardsSelected(numCardsSelected - 1)
                    }}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 10px",
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
                        transition: "all 0.2s ease",
                        minWidth: "60px",
                        border: "1px solid rgba(0, 0, 0, 0.1)"
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)"
                        e.currentTarget.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.4)"
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)"
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.3)"
                    }}
                >
                    <span style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: suitInfo.color,
                        minWidth: "20px"
                    }}>
                        {getCardDisplay(selCard.cardName)}
                    </span>
                    <span style={{
                        fontSize: "18px",
                        color: suitInfo.color
                    }}>
                        {suitInfo.symbol}
                    </span>
                </div>
            )
        }
    }
    return retArr
}

// render information relevant to what's happening in the game (who passed, who played what)
export const renderInfo = (room:Room) => {
    if (room.cardSetFromRound.length > 0 && CardName[room.roundInfo.roundValue] !== "Joker") {
        const lastPlayedCardSet = getLastPlayedCardSet(room)
        const lastPlayedCardSetWithCards = getLastPlayedCardSetWithCards(room)
        return (
            <div style={{ color: "rgba(255,255,255,0.9)" }}>
                {lastPlayedCardSet.deck.getCards().length === 0 ? `${room.order[room.order.length - 1].playerId} passed. \n` : null}
                {lastPlayedCardSetWithCards.player} played {lastPlayedCardSetWithCards.deck.getCount()} {CardName[room.roundInfo.roundValue]}{lastPlayedCardSetWithCards.deck.getCount() > 1 ? 's': ''}.
                Total {CardName[room.roundInfo.roundValue]}{room.cardsInPlay.getCount() > 1 ? 's': ''} played is {room.cardsInPlay.getCount()}.
            </div>
        )
    }
    return null
}

// Render the grouped cards view with fan arrangement
export const renderGroupedCards = (
    cards:PlayingCard[],
    selectedCards:boolean[],
    setCards:(value: React.SetStateAction<PlayingCard[]>) => void,
    setSelectedCards:(value: React.SetStateAction<boolean[]>) => void,
    setSelectedGroupedCard: (value: React.SetStateAction<PlayingCard | undefined>) => void,
    setShowGroupedCardsIsOpen: (value: React.SetStateAction<boolean>) => void,
    showGroupedCardsIsOpen:boolean,
    numCardsSelected:number,
    setNumCardsSelected:React.Dispatch<React.SetStateAction<number>>
) => {
    let retArr: JSX.Element[] = []
    cards.sort((a, b) => {
        if (a.cardName === b.cardName) {
            return 0
        }
        if (a.cardName > b.cardName) {
            return 1
        }
        else return -1
    })
    const firstCards = Array(14).fill(-1)
    for (let i = 0; i < 14; i++) {
        const index = cards.findIndex((card)=>{return card.cardName === i})
        firstCards[i] = index
    }
    let groupedCards = groupCards(cards, selectedCards, setCards, setSelectedCards, numCardsSelected, setNumCardsSelected)
    let ownedCardnameKeySet = Object.keys(groupedCards)
    let validCards = []
    for (let ownedCardnameEnum = 0; ownedCardnameEnum <= 13; ownedCardnameEnum++) {
        if (ownedCardnameKeySet.findIndex((elem) => {return elem === ownedCardnameEnum.toString()}) === -1) {
            continue
        }
        const coverCard = cards[firstCards[ownedCardnameEnum]]
        validCards.push({enum: ownedCardnameEnum, card: coverCard, index: firstCards[ownedCardnameEnum]})
    }

    const numCards = validCards.length
    const maxAngle = Math.min(40, numCards * 2) // Maximum spread angle
    const angleStep = numCards > 1 ? maxAngle / (numCards - 1) : 0
    const startAngle = -maxAngle / 2
    const radius = window.innerWidth > 1400 ? 800 : window.innerWidth * 0.6 // Dynamic radius based on viewport
    const baseBottom = 20 // Base position from bottom

    validCards.forEach((cardData, i) => {
        const angle = startAngle + (i * angleStep)
        const radian = (angle * Math.PI) / 180
        const x = Math.sin(radian) * radius
        const y = Math.cos(radian) * radius - radius

        retArr.push(
            <span
                key={cardData.enum}
                onClick={()=>{
                    setSelectedGroupedCard(cardData.card);
                    setShowGroupedCardsIsOpen(!showGroupedCardsIsOpen)
                }}
                style={{
                    position: "absolute",
                    bottom: baseBottom,
                    left: "50%",
                    transform: `translateX(-50%) translateX(${x}px) translateY(${y}px) rotate(${angle}deg)`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                    zIndex: 10 + i,
                    transformOrigin: "center bottom",
                    display: "inline-block",
                    lineHeight: 0
                }}
            >
                {cardData.index !== -1 ? CardRepresentation(cardData.card.cardName, cardData.card.suit): ""}
            </span>
        )
    })
    return retArr
}

// Populate the modal with the correct cards
export const renderGroupedCardModalContent = (
    cards:PlayingCard[], 
    selectedGroupedCard:PlayingCard | undefined,
    selectedCards:boolean[], 
    setCards:(value: React.SetStateAction<PlayingCard[]>) => void, 
    setSelectedCards:(value: React.SetStateAction<boolean[]>) => void,
    numCardsSelected:number,
    setNumCardsSelected:React.Dispatch<React.SetStateAction<number>>
) => {
    const groupedCards = groupCards(cards, selectedCards, setCards, setSelectedCards, numCardsSelected, setNumCardsSelected)
    if (selectedGroupedCard !== undefined) {
        return groupedCards[selectedGroupedCard.cardName]
    } 
}

// Render all cards view with fan arrangement
export const renderCards = (
    cards:PlayingCard[],
    selectedCards:boolean[],
    setCards:(value: React.SetStateAction<PlayingCard[]>) => void,
    setSelectedCards:(value: React.SetStateAction<boolean[]>) => void,
    numCardsSelected:number,
    setNumCardsSelected:React.Dispatch<React.SetStateAction<number>>,
    cardToIdMap?:Map<PlayingCard, string>
) => {
    let retArr: JSX.Element[] = []
    cards.sort((a, b) => {
        if (a.cardName === b.cardName) {
            return 0
        }
        if (a.cardName > b.cardName) {
            return 1
        }
        else return -1
    })

    // Separate selected and unselected indices
    const unselectedIndices: number[] = []
    const selectedIndices: number[] = []
    for (let i = 0; i < cards.length; i++) {
        if (!selectedCards[i]) {
            unselectedIndices.push(i)
        } else {
            selectedIndices.push(i)
        }
    }

    const numCards = unselectedIndices.length
    const isMobile = window.innerWidth <= 768

    // Mobile: Two-section vertical wrap layout (selected on top, unselected below)
    if (isMobile) {
        return (
            <div style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                overflowY: "auto"
            }}>
                {/* Selected cards section — capped at 40% height so unselected cards remain reachable */}
                {selectedIndices.length > 0 && (
                    <div style={{
                        borderBottom: "2px solid rgba(251, 191, 36, 0.5)",
                        background: "rgba(251, 191, 36, 0.07)",
                        padding: "6px 8px 8px",
                        flexShrink: 0,
                        maxHeight: "40%",
                        overflowY: "auto"
                    }}>
                        <div style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#fbbf24",
                            marginBottom: "6px",
                            textAlign: "center",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase"
                        }}>
                            Selected ({selectedIndices.length}) — tap to deselect
                        </div>
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            justifyContent: "center"
                        }}>
                            {selectedIndices.map((originalIndex) => {
                                const c = cards[originalIndex]
                                const elem = c.cardName === CardName.Joker
                                    ? CardRepresentation(c.cardName, Suit.Spades)
                                    : CardRepresentation(c.cardName, c.suit)
                                const stableKey = cardToIdMap?.get(c) || `card${originalIndex}`
                                return (
                                    <div
                                        key={`sel-${stableKey}`}
                                        onClick={() => {
                                            selectedCards[originalIndex] = false
                                            setCards(cards)
                                            setSelectedCards([...selectedCards])
                                            setNumCardsSelected(numCardsSelected - 1)
                                        }}
                                        style={{
                                            flexShrink: 0,
                                            cursor: "pointer",
                                            outline: "3px solid #fbbf24",
                                            borderRadius: "6px",
                                            transition: "all 0.2s ease",
                                            boxShadow: "0 0 10px rgba(251, 191, 36, 0.35)"
                                        }}
                                    >
                                        {elem}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Unselected cards section — tap any to select */}
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    justifyContent: "center",
                    padding: "8px",
                    overflowY: "auto",
                    flex: 1,
                    alignContent: "flex-start"
                }}>
                    {unselectedIndices.map((originalIndex) => {
                        const c = cards[originalIndex]
                        const elem = c.cardName === CardName.Joker
                            ? CardRepresentation(c.cardName, Suit.Spades)
                            : CardRepresentation(c.cardName, c.suit)
                        const stableKey = cardToIdMap?.get(c) || `card${originalIndex}`
                        return (
                            <div
                                key={stableKey}
                                id={`card${originalIndex.toString()}`}
                                onClick={() => {
                                    selectedCards[originalIndex] = true
                                    setCards(cards)
                                    setSelectedCards([...selectedCards])
                                    setNumCardsSelected(numCardsSelected + 1)
                                }}
                                style={{
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
                                    borderRadius: "8px"
                                }}
                            >
                                {elem}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    // Desktop: Fan layout
    const maxAngle = Math.min(40, numCards * 1.5)
    const angleStep = numCards > 1 ? maxAngle / (numCards - 1) : 0
    const startAngle = -maxAngle / 2
    const radius = window.innerWidth > 1400 ? 800 : window.innerWidth * 0.6  // Dynamic radius based on viewport
    const baseBottom = 20

    unselectedIndices.forEach((originalIndex, displayIndex) => {
        let c = cards[originalIndex]
        let elem = null
        if (c.cardName === CardName.Joker) {
            elem = CardRepresentation(c.cardName, Suit.Spades)
        } else {
            elem = CardRepresentation(c.cardName, c.suit)
        }

        const angle = startAngle + (displayIndex * angleStep)
        const radian = (angle * Math.PI) / 180
        const x = Math.sin(radian) * radius
        const y = Math.cos(radian) * radius - radius

        const stableKey = cardToIdMap?.get(c) || `card${originalIndex}`

        retArr.push(
            <span
                key={stableKey}
                id={`card${originalIndex.toString()}`}
                onClick={() => {
                    selectedCards[originalIndex] = !selectedCards[originalIndex]
                    setCards(cards)
                    setSelectedCards(selectedCards)
                    const changeNumSelectedCards = changeBorder(document.getElementById(`card${originalIndex.toString()}`), selectedCards[originalIndex])
                    setNumCardsSelected(numCardsSelected + changeNumSelectedCards)
                }}
                style={{
                    position: "absolute",
                    bottom: baseBottom,
                    left: "50%",
                    transform: `translateX(-50%) translateX(${x}px) translateY(${y}px) rotate(${angle}deg)`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                    zIndex: 10 + displayIndex,
                    transformOrigin: "center bottom",
                    display: "inline-block",
                    lineHeight: 0
                }}
                >
                {elem}
            </span>
        )
    })
    return retArr
}